import api from "./apiClient";

/* ===================================================== */
/* 🔧 CONSTANTS                                          */
/* ===================================================== */

const DEFAULT_VISIBILITY = "internal";
const DEFAULT_COLOR = "#2563EB";

const ALLOWED_VISIBILITIES = new Set([
  "internal",
  "client",
  "public",
]);

const ALLOWED_STATUSES = new Set([
  "planned",
  "completed",
  "cancelled",
]);

/* ===================================================== */
/* 🔧 ERROR NORMALIZER                                   */
/* ===================================================== */

function normalizeError(
  err,
  fallbackMessage
) {
  return new Error(
    err?.response?.data?.message ||
      err?.message ||
      fallbackMessage
  );
}

/* ===================================================== */
/* 🔧 VALIDATION HELPERS                                 */
/* ===================================================== */

function isNonEmptyString(value) {
  return (
    typeof value === "string" &&
    value.trim().length > 0
  );
}

function requireObjectPayload(
  payload,
  message
) {
  if (
    payload === null ||
    typeof payload !== "object" ||
    Array.isArray(payload)
  ) {
    throw new Error(message);
  }
}

/* ===================================================== */
/* 🔧 DATE NORMALIZER                                    */
/* ===================================================== */

function normalizeTargetDate(value) {
  if (value === undefined) {
    return undefined;
  }

  if (
    value === null ||
    value === ""
  ) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    throw new Error(
      "Milestone target date is invalid"
    );
  }

  return value;
}

/* ===================================================== */
/* 🔧 ORDER NORMALIZER                                   */
/* ===================================================== */

function normalizeOrder(value) {
  if (value === undefined) {
    return undefined;
  }

  if (
    value === null ||
    value === ""
  ) {
    return 0;
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    throw new Error(
      "Milestone order must be a valid number"
    );
  }

  return numericValue;
}

/* ===================================================== */
/* 🔧 VISIBILITY NORMALIZER                              */
/* ===================================================== */

function normalizeVisibility(value) {
  if (value === undefined) {
    return undefined;
  }

  if (
    typeof value !== "string" ||
    !ALLOWED_VISIBILITIES.has(value)
  ) {
    throw new Error(
      "Invalid milestone visibility"
    );
  }

  return value;
}

/* ===================================================== */
/* 🔧 STATUS NORMALIZER                                  */
/* ===================================================== */

function normalizeStatus(value) {
  if (value === undefined) {
    return undefined;
  }

  if (
    typeof value !== "string" ||
    !ALLOWED_STATUSES.has(value)
  ) {
    throw new Error(
      "Invalid milestone status"
    );
  }

  return value;
}

/* ===================================================== */
/* 🔧 COLOR NORMALIZER                                   */
/* ===================================================== */

function normalizeColor(value) {
  if (value === undefined) {
    return undefined;
  }

  if (
    typeof value !== "string" ||
    !value.trim()
  ) {
    throw new Error(
      "Milestone color cannot be empty"
    );
  }

  return value.trim();
}

/* ===================================================== */
/* 🔧 SHARED USERS NORMALIZER                            */
/* ===================================================== */

function normalizeSharedWith(value) {
  if (value === undefined) {
    return undefined;
  }

  if (!Array.isArray(value)) {
    throw new Error(
      "sharedWith must be an array"
    );
  }

  const result = [];
  const seen = new Set();

  for (const userId of value) {
    if (
      userId === null ||
      userId === undefined ||
      String(userId).trim() === ""
    ) {
      throw new Error(
        "sharedWith contains an invalid user id"
      );
    }

    const normalizedId =
      String(userId).trim();

    if (!seen.has(normalizedId)) {
      seen.add(normalizedId);
      result.push(normalizedId);
    }
  }

  return result;
}

/* ===================================================== */
/* 🔧 UPDATE PAYLOAD BUILDER                             */
/* ===================================================== */

/**
 * The backend supports partial milestone updates.
 *
 * Only properties explicitly supplied by the caller
 * are sent to the backend.
 *
 * This prevents accidental resets such as:
 *
 * status       → planned
 * visibility   → internal
 * color        → default blue
 *
 * when the caller only intended to update the title.
 */
function buildUpdatePayload(payload) {
  requireObjectPayload(
    payload,
    "Milestone update payload is required"
  );

  const body = {};

  /* --------------------------------------------------- */
  /* TITLE                                               */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "title"
    )
  ) {
    if (
      !isNonEmptyString(
        payload.title
      )
    ) {
      throw new Error(
        "Milestone title cannot be empty"
      );
    }

    body.title =
      payload.title.trim();
  }

  /* --------------------------------------------------- */
  /* SUMMARY                                             */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "summary"
    )
  ) {
    if (
      payload.summary === null
    ) {
      body.summary = "";
    } else if (
      typeof payload.summary !==
      "string"
    ) {
      throw new Error(
        "Milestone summary must be a string"
      );
    } else {
      body.summary =
        payload.summary.trim();
    }
  }

  /* --------------------------------------------------- */
  /* TARGET DATE                                         */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "targetDate"
    )
  ) {
    body.targetDate =
      normalizeTargetDate(
        payload.targetDate
      );
  }

  /* --------------------------------------------------- */
  /* VISIBILITY                                          */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "visibility"
    )
  ) {
    body.visibility =
      normalizeVisibility(
        payload.visibility
      );
  }

  /* --------------------------------------------------- */
  /* STATUS                                              */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "status"
    )
  ) {
    body.status =
      normalizeStatus(
        payload.status
      );
  }

  /* --------------------------------------------------- */
  /* COLOR                                               */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "color"
    )
  ) {
    body.color =
      normalizeColor(
        payload.color
      );
  }

  /* --------------------------------------------------- */
  /* ORDER                                               */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "order"
    )
  ) {
    body.order =
      normalizeOrder(
        payload.order
      );
  }

  /* --------------------------------------------------- */
  /* SHARED WITH                                         */
  /* --------------------------------------------------- */

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "sharedWith"
    )
  ) {
    body.sharedWith =
      normalizeSharedWith(
        payload.sharedWith
      );
  }

  /* --------------------------------------------------- */
  /* EMPTY UPDATE GUARD                                  */
  /* --------------------------------------------------- */

  if (
    Object.keys(body).length === 0
  ) {
    throw new Error(
      "At least one milestone field is required for update"
    );
  }

  return body;
}

/* ===================================================== */
/* 📋 LIST MILESTONES                                    */
/* GET /milestones/:projectId                            */
/* ===================================================== */

export const listMilestones = async (
  projectId
) => {
  if (!projectId) {
    throw new Error(
      "Project ID is required"
    );
  }

  try {
    const { data } =
      await api.get(
        `/milestones/${encodeURIComponent(
          String(projectId)
        )}`
      );

    return Array.isArray(data)
      ? data
      : [];
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to fetch milestones"
    );
  }
};

/* ===================================================== */
/* ➕ CREATE MILESTONE                                   */
/* POST /milestones                                      */
/* ===================================================== */

export const createMilestone = async (
  payload
) => {
  requireObjectPayload(
    payload,
    "Milestone payload is required"
  );

  /* --------------------------------------------------- */
  /* TITLE                                               */
  /* --------------------------------------------------- */

  if (
    !isNonEmptyString(
      payload.title
    )
  ) {
    throw new Error(
      "Milestone title is required"
    );
  }

  /* --------------------------------------------------- */
  /* PROJECT                                             */
  /* --------------------------------------------------- */

  if (!payload.projectId) {
    throw new Error(
      "Project ID is required"
    );
  }

  try {
    /* ------------------------------------------------- */
    /* TARGET DATE                                       */
    /* ------------------------------------------------- */

    const normalizedTargetDate =
      normalizeTargetDate(
        payload.targetDate
      );

    /* ------------------------------------------------- */
    /* VISIBILITY                                        */
    /* ------------------------------------------------- */

    const normalizedVisibility =
      payload.visibility === undefined
        ? DEFAULT_VISIBILITY
        : normalizeVisibility(
            payload.visibility
          );

    /* ------------------------------------------------- */
    /* COLOR                                             */
    /* ------------------------------------------------- */

    const normalizedColor =
      payload.color === undefined
        ? DEFAULT_COLOR
        : normalizeColor(
            payload.color
          );

    /* ------------------------------------------------- */
    /* ORDER                                             */
    /* ------------------------------------------------- */

    const normalizedOrder =
      payload.order === undefined
        ? 0
        : normalizeOrder(
            payload.order
          );

    /* ------------------------------------------------- */
    /* SHARED WITH                                       */
    /* ------------------------------------------------- */

    const normalizedSharedWith =
      normalizeSharedWith(
        payload.sharedWith
      );

    /* ------------------------------------------------- */
    /* REQUEST BODY                                      */
    /* ------------------------------------------------- */

    const body = {
      projectId:
        payload.projectId,

      title:
        payload.title.trim(),

      summary:
        payload.summary === undefined
          ? ""
          : payload.summary === null
            ? ""
            : typeof payload.summary ===
              "string"
              ? payload.summary.trim()
              : (() => {
                  throw new Error(
                    "Milestone summary must be a string"
                  );
                })(),

      targetDate:
        normalizedTargetDate ===
        undefined
          ? null
          : normalizedTargetDate,

      visibility:
        normalizedVisibility,

      color:
        normalizedColor,

      order:
        normalizedOrder,

      ...(normalizedSharedWith !==
        undefined && {
        sharedWith:
          normalizedSharedWith,
      }),
    };

    /* ------------------------------------------------- */
    /* API REQUEST                                       */
    /* ------------------------------------------------- */

    const { data } =
      await api.post(
        "/milestones",
        body
      );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to create milestone"
    );
  }
};

/* ===================================================== */
/* ✏️ UPDATE MILESTONE                                   */
/* PUT /milestones/:id                                   */
/* ===================================================== */

export const updateMilestone = async (
  milestoneId,
  payload
) => {
  if (!milestoneId) {
    throw new Error(
      "Milestone ID is required"
    );
  }

  try {
    const body =
      buildUpdatePayload(
        payload
      );

    const { data } =
      await api.put(
        `/milestones/${encodeURIComponent(
          String(milestoneId)
        )}`,
        body
      );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to update milestone"
    );
  }
};

/* ===================================================== */
/* ❌ DELETE MILESTONE                                   */
/* DELETE /milestones/:id                                */
/* ===================================================== */

export const deleteMilestone = async (
  milestoneId
) => {
  if (!milestoneId) {
    throw new Error(
      "Milestone ID is required"
    );
  }

  try {
    const { data } =
      await api.delete(
        `/milestones/${encodeURIComponent(
          String(milestoneId)
        )}`
      );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to delete milestone"
    );
  }
};

/* ===================================================== */
/* 🔗 SHARE MILESTONE                                    */
/* ===================================================== */

/**
 * Sharing is intentionally unavailable until the
 * backend exposes a dedicated sharing endpoint.
 */
export const shareMilestone = async () => {
  throw new Error(
    "shareMilestone is not supported by the current backend"
  );
};

/* ===================================================== */
/* EXPORT                                                */
/* ===================================================== */

const milestoneService = {
  listMilestones,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  shareMilestone,
};

export default milestoneService;